import { isObject, pickBy } from 'lodash-es';
import type { Simplify, UnknownRecord } from 'type-fest';

/**
 * Recursively strip `| undefined` from all values in an object.
 *
 * Replaces any potentially `undefined` values with an
 * optional `key` and a non-optional value.
 *
 * TODO: add SimplifyDeep from type-fest after we've updated.
 * TODO: ensure objects inside arrays are also stripped.
 * TODO: likely doesn't work 100% with optional object values
 */
type WithoutUndefinedValues<T extends UnknownRecord> = Simplify<
  {
    [K in keyof T as undefined extends T[K]
      ? K
      : never]?: T[K] extends UnknownRecord
      ? WithoutUndefinedValues<T[K]>
      : NonNullable<T[K]>;
  } & {
    [K in keyof T as undefined extends T[K]
      ? never
      : K]-?: T[K] extends UnknownRecord ? WithoutUndefinedValues<T[K]> : T[K];
  }
>;

/**
 * Strip all of the undefined and null values from an object (recursively)
 *
 * This includes undefined from objects within nested arrays
 *
 * This function is intended to be used in all toRawXYZ functions and likely shouldn't be used anywhere
 * else (double-check with someone else before you use this outside of a toRawXYZ)
 */
export const stripUndefined = <T extends UnknownRecord>(
  obj: T
): WithoutUndefinedValues<T> => {
  // clean the current object
  const cleanedObj = pickBy(
    obj,
    (val) => val !== undefined && val !== null
  ) as T;
  // recursively clean all sub-objects
  for (const [key, value] of Object.entries(cleanedObj) as [
    keyof T,
    T[keyof T],
  ][]) {
    if (isObject(value)) {
      cleanedObj[key] = stripUndefinedInternalObject(value);
    }
  }
  return cleanedObj as unknown as WithoutUndefinedValues<T>;
};

/**
 * Used internally for stripUndefined's recursion logic.
 *
 * Should only be called on values that pass isObject()
 *
 * If the value is an object, it is either an array or another object.
 * If it is an array, recursively run stripUndefinedInternalObject on all values that are objects.
 *
 * Otherwise, just run stripUndefined recursively
 */
const stripUndefinedInternalObject = <T extends object>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map((item) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      isObject(item) ? stripUndefinedInternalObject(item) : item
    ) as T;
  }
  return stripUndefined(obj as Record<string, unknown>) as T;
};
